import React from 'react';
import { useTranslation } from 'react-i18next';

const money = (cents, currency) => (cents === null || cents === undefined
  ? '—'
  : `${(cents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} ${currency || '€'}`);

/**
 * One shop label, as read.
 *
 * The two prices are shown side by side rather than merged: which one a shopper pays depends on a
 * condition, and collapsing them would hide it. What the label does not say stays visibly empty.
 */
const LabelCard = ({ label }) => {
  const { t } = useTranslation();
  const promoted = label.promotional_price_cents !== null && label.promotional_price_cents !== undefined;

  return (
    <div className="rounded-xl border border-gray-200 p-3 dark:border-gray-700">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-medium text-gray-900 dark:text-white">
          {label.brand_raw || t('unknown')} {label.reference_raw && <code className="text-xs text-gray-500">{label.reference_raw}</code>}
        </span>
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300">
          {t(`label_type.${label.label_type}`, { defaultValue: label.label_type })}
        </span>
      </div>

      {label.name_raw && <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{label.name_raw}</p>}

      <div className="mt-2 flex flex-wrap items-baseline gap-3">
        {promoted ? (
          <>
            <span className="text-lg font-semibold text-green-700 dark:text-green-300">
              {money(label.promotional_price_cents, label.currency)}
            </span>
            <span className="text-sm text-gray-500 line-through">{money(label.regular_price_cents, label.currency)}</span>
          </>
        ) : (
          <span className="text-lg font-semibold text-gray-900 dark:text-white">
            {money(label.regular_price_cents, label.currency)}
          </span>
        )}
        {label.energy_class && (
          <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100">
            {t('Energy class {{class}}', { class: label.energy_class })}
          </span>
        )}
      </div>

      {label.promotion && (
        <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
          {t(`promotion.${label.promotion.mechanism}`, { defaultValue: label.promotion.mechanism })}
          {label.promotion.requires_membership === true && ` · ${t('requires a loyalty card')}`}
          {label.promotion.condition_raw && ` · « ${label.promotion.condition_raw} »`}
          {label.promotion.rebate_amount_cents != null && ` · ${t('rebate {{amount}}', { amount: money(label.promotion.rebate_amount_cents, label.currency) })}`}
        </p>
      )}

      {/* What anyone pays today, versus the best price in store — the derived series. */}
      <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
        <span>{t('Public price')} : {money(label.public_price_cents, label.currency)}</span>
        <span>{t('Best price')} : {money(label.best_price_cents, label.currency)}</span>
        {label.net_after_rebate_cents != null && (
          <span>{t('Net after rebate')} : {money(label.net_after_rebate_cents, label.currency)}</span>
        )}
      </div>

      {label.identifiers.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {label.identifiers.map(identifier => (
            <code key={`${identifier.type}-${identifier.value}`} className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-600 dark:bg-gray-800 dark:text-gray-300">
              {identifier.type}: {identifier.value}
            </code>
          ))}
        </div>
      )}

      {label.other_prices.length > 0 && (
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          {label.other_prices.map(entry => `${t(`other_price.${entry.type}`, { defaultValue: entry.type })} ${money(entry.amount_cents, label.currency)}`).join(' · ')}
        </p>
      )}

      {(label.unreadable_fields.length > 0 || label.missing_fields.length > 0 || label.warnings.length > 0) && (
        <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
          {label.unreadable_fields.length > 0 && `${t('Unreadable')} : ${label.unreadable_fields.join(', ')}. `}
          {label.missing_fields.length > 0 && `${t('Absent')} : ${label.missing_fields.join(', ')}. `}
          {label.warnings.join(' · ')}
        </p>
      )}
    </div>
  );
};

export default LabelCard;
